import React, { useEffect, useState } from "react";
import { Wifi, WifiOff, CloudUpload, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { countOps } from "@/lib/offlineQueue";
import { flushQueue } from "@/lib/offlineSync";
import { subscribeSync, setSyncState, SYNC } from "@/lib/syncState";
import SyncFailures from "@/components/SyncFailures";

export default function OfflineBanner() {
  const [st, setSt] = useState({ status: navigator.onLine ? SYNC.ONLINE : SYNC.OFFLINE, pending: 0 });

  useEffect(() => {
    const unsub = subscribeSync(setSt);
    const refresh = () => countOps().then((p) => setSyncState({ pending: p })).catch(() => {});
    const onOnline = () => { setSyncState({ status: SYNC.ONLINE }); flushQueue().then(refresh); };
    const onOffline = () => setSyncState({ status: SYNC.OFFLINE });
    const onQueued = () => refresh();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queued", onQueued);
    refresh();
    if (navigator.onLine) flushQueue().then(refresh);
    const iv = setInterval(() => { if (navigator.onLine) flushQueue().then(refresh); }, 30000);
    return () => {
      clearInterval(iv); unsub();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queued", onQueued);
    };
  }, []);

  // Auto-fade the green "Synchronisiert" confirmation back to the steady ONLINE state.
  useEffect(() => {
    if (st.status === SYNC.SYNCED) {
      const t = setTimeout(() => setSyncState({ status: SYNC.ONLINE }), 3000);
      return () => clearTimeout(t);
    }
  }, [st.status]);

  const { status, pending } = st;

  // Resolve the effective display state. When ONLINE but with pending ops we show the amber "waiting" variant.
  let view = status;
  if (status === SYNC.ONLINE && pending > 0) view = "PENDING";

  const CFG = {
    [SYNC.ONLINE]: { bg: "bg-emerald-600", dot: "bg-emerald-300", Icon: Wifi, spin: false, text: "Online" },
    [SYNC.OFFLINE]: { bg: "bg-red-600", dot: "bg-red-300", Icon: WifiOff, spin: false, text: "Offline – Änderungen werden lokal gespeichert" },
    [SYNC.SYNCING]: { bg: "bg-amber-500", dot: "bg-amber-200", Icon: RefreshCw, spin: true, text: "Synchronisierung läuft…" },
    [SYNC.SYNCED]: { bg: "bg-emerald-600", dot: "bg-emerald-300", Icon: CheckCircle2, spin: false, text: "Synchronisiert" },
    [SYNC.SYNC_ERROR]: { bg: "bg-red-600", dot: "bg-red-300", Icon: AlertTriangle, spin: false, text: "Synchronisierung benötigt Aufmerksamkeit" },
    PENDING: { bg: "bg-amber-500", dot: "bg-amber-200", Icon: CloudUpload, spin: false, text: `${pending} Änderung(en) warten auf Synchronisierung` },
  };
  const c = CFG[view] || CFG[SYNC.ONLINE];
  const showSync = (view === "PENDING" || view === SYNC.SYNC_ERROR) && pending > 0;

  return (
    <div data-testid="status-bar" data-sync-status={status}
      className={`w-full text-xs font-medium px-4 py-1 flex items-center justify-center gap-2 text-white transition-colors duration-300 ${c.bg}`}>
      <span className={`h-2 w-2 rounded-full ${c.dot} ${c.spin ? "animate-pulse" : ""}`} data-testid="status-dot" />
      <c.Icon className={`h-3.5 w-3.5 ${c.spin ? "animate-spin" : ""}`} />
      <span data-testid="status-text">{c.text}</span>
      {pending > 0 && view !== SYNC.SYNCING && view !== "PENDING" && (
        <span className="opacity-90">· <span data-testid="offline-pending-count">{pending}</span> ausstehend</span>
      )}
      {view === "PENDING" && (
        <span className="opacity-90 sr-only"><span data-testid="offline-pending-count">{pending}</span></span>
      )}
      {showSync && (
        <button onClick={() => flushQueue()} className="underline ml-1 font-semibold" data-testid="offline-sync-btn">
          Jetzt synchronisieren
        </button>
      )}
      <SyncFailures />
    </div>
  );
}
